if (!customElements.get('campaigns-element')) {
  customElements.define(
    'campaigns-element',
    class CampaignsElement extends HTMLElement {
      // Cross-instance leader. Only the worker mutates the cart
      // (addToCart / updateCart). Non-worker instances still refresh
      // their own innerHTML so the drawer/PDP block UI stays in sync.
      static activeWorker = null;

      constructor() {
        super();
        this.instanceId = Math.random().toString(36).slice(2);
        this.sectionId = this.dataset.sectionId;
        this.campaigns = {};
        this.activeCampaign = null;
        this.cartUpdated = false;
        this.cartUpdateFailure = false;
        this.prevCampaigns = this.querySelectorAll('.campaign-block');
        this.cart = document.querySelector('cart-notification') || document.querySelector('drawer-cart');
        this.inCart = window.location.pathname === '/cart';
        this.updateInProgress = false;
        this.rerunRequested = false;
        this.giftExcess = false;
        this.debounceTimer = null;

        // Bind event handlers to preserve context
        this.handleAjaxProductAdded = this.onCartUpdate.bind(this);
        this.handleCartUpdate = this.onCartUpdate.bind(this);

        // Claim leadership eagerly so the initial checkCart only runs once
        // even when multiple instances co-exist (drawer + PDP block).
        if (!CampaignsElement.activeWorker) {
          CampaignsElement.activeWorker = this;
        }

        if (this.isWorker) {
          this.runExclusive(() => this.initialReconcile());
        }
      }

      connectedCallback() {
        // Re-claim leadership if the previous leader was disconnected
        // before this instance was attached.
        if (!CampaignsElement.activeWorker) {
          CampaignsElement.activeWorker = this;
        }
        document.addEventListener('ajaxProduct:added', this.handleAjaxProductAdded);
        document.addEventListener('cart:update', this.handleCartUpdate);
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this.handleAjaxProductAdded);
        document.removeEventListener('cart:update', this.handleCartUpdate);
        if (CampaignsElement.activeWorker === this) {
          const others = document.querySelectorAll('campaigns-element');
          const next = Array.from(others).find((el) => el !== this);
          CampaignsElement.activeWorker = next || null;
        }
      }

      get isWorker() {
        return CampaignsElement.activeWorker === this;
      }

      /**
       * Handles cart update events
       * @param {Event} event - The cart update event
       */
      async onCartUpdate(event) {
        // Optimisation only — skip events this instance just dispatched.
        // Correctness does not depend on this gate: the leader refreshes
        // its own innerHTML inside processCartUpdate (both before and
        // after its cart mutations), so it does not need a second pass
        // triggered by its own event. Skipping it just avoids a wasted
        // section fetch and a brief checkout-button flicker. Other
        // instances always process the event because their instanceId
        // differs, so their UI still stays in sync.
        if (event?.detail?.from === this.instanceId) {
          return;
        }
        this.debounce(() => this.processCartUpdate());
      }

      /**
       * Debounces function calls
       * @param {Function} callback - Function to call after debounce delay
       * @param {number} delay - Delay in ms
       */
      debounce(callback, delay = 300) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          callback();
        }, delay);
      }

      /**
       * Processes cart updates with loading state management
       */
      processCartUpdate() {
        return this.runExclusive(() => this.reconcile());
      }

      /**
       * Runs a reconciliation pass with the checkout buttons locked, never
       * letting two passes overlap. Overlapping passes both read the cart
       * before either had added the gift, so each added one (2 gifts with a
       * limit of 1). A cart change that arrives mid-pass is not dropped: it
       * queues one more full pass once the current one finishes.
       * @param {Function} firstPass - async pass to run first
       */
      async runExclusive(firstPass) {
        if (this.updateInProgress) {
          this.rerunRequested = true;
          return;
        }
        this.updateInProgress = true;

        // Only the leader locks the checkout buttons. Non-leaders just
        // refresh innerHTML.
        if (this.isWorker) {
          this.setCheckoutLocked(true);
        }

        try {
          let pass = firstPass;
          // Bounded so a cart that keeps failing to settle cannot loop forever.
          for (let i = 0; pass && i < 3; i++) {
            this.rerunRequested = false;
            // Per-pass state: a failure in an earlier pass must not skip
            // checkCart or keep checkout locked once the cart has settled.
            this.cartUpdateFailure = false;
            this.giftExcess = false;
            await pass();
            pass = this.rerunRequested ? () => this.reconcile() : null;
          }
        } catch (error) {
          console.error('Error updating campaigns:', error);
          this.showWarningMessage('Failed to update campaigns. Please try again.');
        } finally {
          this.updateInProgress = false;
          // Keep checkout locked while the cart still holds more gifts than
          // allowed, so a failed reduction cannot be checked out.
          if (this.isWorker) {
            this.setCheckoutLocked(this.giftExcess);
          }
        }
      }

      /**
       * Disables or re-enables the cart checkout buttons. cart.js re-applies
       * the lock after re-rendering the footer while campaignsProcessing is set.
       * @param {boolean} locked
       */
      setCheckoutLocked(locked) {
        window.campaignsProcessing = locked;
        document.querySelectorAll('button[type="submit"][form="cart"], shopify-paypal-button, shopify-apple-pay-button').forEach((button) => {
          button.disabled = locked;
          if (!locked) button.removeAttribute('data-pending-validation');
        });
      }

      /**
       * One full pass: refresh the campaign blocks, then (leader only) bring
       * the cart's gift lines in line with them.
       */
      async reconcile() {
        const refreshed = await this.refreshSectionHTML();
        if (!refreshed) return;

        // Only the leader mutates the cart. With multiple instances
        // (drawer + PDP block) on a PDP, running these on every
        // instance caused a 1 → 2 → 1 gift-quantity bounce.
        if (!this.isWorker) return;

        await this.removeIneligibleGifts();
        await this.removeOrphanGifts();

        if (!this.cartUpdateFailure) {
          await this.checkCart();
        }

        // The leader just mutated the cart, but its own innerHTML
        // is still showing the pre-mutation render captured by the
        // first refreshSectionHTML() call above. Non-leader
        // instances refresh themselves via the ajaxProduct:added
        // event dispatched from checkCart / removeIneligibleGifts,
        // but the leader ignores its own dispatched events
        // (see onCartUpdate). Refresh explicitly so the leader's
        // campaign-block UI matches the post-mutation cart state.
        await this.refreshSectionHTML();
      }

      /**
       * Fetches the campaigns section and swaps this element's innerHTML
       * to reflect the latest server-rendered state. Captures the
       * pre-swap DOM into prevCampaigns so removeIneligibleGifts can
       * diff against it.
       * @returns {Promise<boolean>} - true if the swap succeeded
       */
      async refreshSectionHTML() {
        // Promotions section is cart-template only (enabled_on: cart).
        // Fetching from `/` on PDP/drawer returns no campaigns-element.
        const sectionRoute = routes.cart_url || '/cart';

        const response = await fetch(`${sectionRoute}?section_id=campaigns`);
        if (!response.ok) throw new Error(`Failed to fetch campaigns: ${response.status}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('campaigns-element');

        if (!sourceQty) {
          console.warn('No campaigns-element found in response');
          return false;
        }

        this.prevCampaigns = this.querySelectorAll('.campaign-block');
        this.innerHTML = sourceQty.innerHTML;
        return true;
      }

      /**
       * Removes gifts that are no longer eligible
       */
      async removeIneligibleGifts() {
        const campaignBlocks = this.querySelectorAll('.campaign-block');
        if (this.prevCampaigns.length > campaignBlocks.length) {
          const prevGiftsArr = Array.from(this.prevCampaigns);
          const prevGiftsIdsData = prevGiftsArr.map((item) => item.dataset.campaignGiftVariantIds);
          const prevGiftsIds =
            prevGiftsIdsData
              .map((item) => item.split(','))
              ?.flat()
              ?.filter((arr) => arr) || [];
          const giftsIdsData = Array.from(campaignBlocks).map((item) => item.dataset.campaignGiftVariantIds);
          const giftIds =
            giftsIdsData
              .map((item) => item.split(','))
              ?.flat()
              ?.filter((arr) => arr) || [];
          const giftIdsSet = new Set(giftIds.map((number) => number));
          const giftIdsToRemove = prevGiftsIds.filter((number) => !giftIdsSet.has(number));
          if (giftIdsToRemove.length == 0) {
            return true;
          } else {
            const cartData = await this.updateCart(null, 0, giftIdsToRemove);
            if (cartData) this.dispatchCartUpdated(cartData);
            return false;
          }
        }
        return true;
      }

      /**
       * Reconciles the live cart against the campaign blocks rendered right
       * now and removes any gift line that no longer belongs to a currently
       * eligible campaign.
       *
       * Unlike removeIneligibleGifts (which only reacts when a campaign block
       * disappears between two renders), this reads /cart.js directly, so it
       * also clears "orphan" gifts that were already in the cart with no
       * qualifying product - e.g. on a cold page load, or after the customer
       * removes the last eligible item. Without this, such gifts can never be
       * removed because their campaign block is never rendered
       * (campaign_product_count is 0) and checkCart returns early.
       * @returns {Promise<boolean>} true if any gift line was removed
       */
      async removeOrphanGifts() {
        const cart = await this.fetchCart();
        if (!cart) return false;

        // Campaigns that currently render a block are still eligible. Any gift
        // whose _campaign is not in this set is an orphan and must be removed.
        const activeCampaigns = new Set(
          Array.from(this.querySelectorAll('.campaign-block')).map((block) => block.dataset.campaignName)
        );

        const orphanKeys = (cart.items || [])
          .filter((item) => {
            const properties = item.properties || {};
            const isGift = properties._campaign || properties._automatic_gift;
            if (!isGift) return false;
            return !properties._campaign || !activeCampaigns.has(properties._campaign);
          })
          .map((item) => item.key);

        if (!orphanKeys.length) return false;

        const updates = orphanKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
        const cartData = await this.postCartUpdate(updates);
        if (!cartData) return false;

        this.dispatchCartUpdated(cartData);
        return true;
      }

      /**
       * Initial reconciliation run once by the leader on load. On the cart
       * page the rendered blocks reflect the full, unfiltered set of eligible
       * campaigns, so orphan gifts can be reconciled immediately. On other
       * surfaces (e.g. a PDP where the block set is filtered to the current
       * product) we defer orphan cleanup to the first cart event, which
       * refetches the global campaigns section before reconciling.
       */
      async initialReconcile() {
        if (this.inCart) {
          await this.removeOrphanGifts();
        }
        await this.checkCart();
      }

      /**
       * Shows warning message to the user
       * @param {string} message - Optional custom message to display
       */
      showWarningMessage(message) {
        const warningMessage = document.querySelector('.warning-message');
        if (warningMessage) {
          if (message && warningMessage.querySelector('.message-content')) {
            warningMessage.querySelector('.message-content').textContent = message;
          }
          warningMessage.classList.remove('hidden');
        }
      }

      /**
       * Hides warning message
       */
      hideWarningMessage() {
        const warningMessage = document.querySelector('.warning-message');
        if (warningMessage) {
          warningMessage.classList.add('hidden');
        }
      }

      /**
       * Checks cart and updates campaign gifts based on eligibility
       */
      async checkCart() {
        const campaignBlocks = this.querySelectorAll('.campaign-block');
        if (!campaignBlocks.length) return;

        const liveCart = await this.fetchCart();

        let cartData;
        this.campaigns = {};
        this.activeCampaign = null;
        let cartUpdated = false;

        for (const campaignElem of campaignBlocks) {
          try {
            // If true, user will select prize manually from choices
            const prizeSelection = campaignElem.dataset.prizeSelection === 'true';
            // Campaign name
            const campaignName = campaignElem.dataset.campaignName;
            // Variant id and key of the prize product
            const giftVariantId = campaignElem.dataset.campaignGiftVariantId;

            // How many gift products the user is eligible for
            const campaignEligibleAmount = parseInt(campaignElem.dataset.campaignEligibleAmount, 10) || 0;
            const prizesInCartFromDom = parseInt(campaignElem.dataset.campaignPrizesInCart, 10) || 0;
            const giftLines = liveCart ? this.getCampaignGiftLines(liveCart, campaignName, giftVariantId) : null;
            const prizesInCart = giftLines
              ? giftLines.reduce((sum, item) => sum + (item.quantity || 0), 0)
              : prizesInCartFromDom;
            // How many prize products should be in cart
            let prizeQuantity = campaignEligibleAmount - prizesInCart;

            if (!prizeSelection && prizeQuantity > 0) {
              cartData = await this.addToCart(giftVariantId, prizeQuantity, campaignName);
              if (cartData) {
                cartUpdated = true;
              }
            } else if (prizeQuantity < 0) {
              const trimmed = giftLines
                ? await this.trimGiftLines(giftLines, -prizeQuantity)
                : await this.updateCart(giftVariantId, campaignEligibleAmount);
              if (trimmed) {
                cartData = trimmed;
                cartUpdated = true;
              } else {
                this.giftExcess = true;
              }
            }
          } catch (error) {
            console.error(`Error processing campaign block:`, error);
          }
        }

        // Send a single cart update event after processing all campaigns
        if (cartUpdated && cartData) {
          this.dispatchCartUpdated(cartData);
        }

        return cartData;
      }

      /**
       * Returns the live cart's gift lines for a campaign.
       * Uses line-item properties written by addToCart / prize cards, not the
       * server-rendered data-campaign-prizes-in-cart attribute, so a page load
       * cannot re-add a gift that Liquid failed to classify.
       */
      getCampaignGiftLines(cart, campaignName, giftVariantId) {
        return (cart.items || []).filter((item) => {
          const properties = item.properties || {};
          const campaign = properties._campaign;
          // A line that names a different campaign belongs to that campaign, even
          // when both campaigns hand out the same gift variant.
          if (campaign) return campaign === campaignName;
          // Lines with no `_campaign` predate the property being written by
          // addToCart, so fall back to matching the gift variant.
          return (
            Boolean(properties._automatic_gift) &&
            Boolean(giftVariantId) &&
            String(item.variant_id) === String(giftVariantId)
          );
        });
      }

      /**
       * Removes `excess` gift units from a campaign's lines, addressing each
       * line by its key. Updating by variant id only touches the first line of
       * that variant, so two gift lines of the same variant could never be
       * reduced. If Shopify rejects the reduction (e.g. a sold-out gift cannot
       * be set to a non-zero quantity), the campaign's gift lines are removed
       * outright, which always succeeds, and another pass re-adds what is still
       * allowed.
       * @param {Array} giftLines - lines from getCampaignGiftLines
       * @param {number} excess - gift units over the allowed amount
       * @returns {Promise<Object|null>} - cart data, or null if nothing worked
       */
      async trimGiftLines(giftLines, excess) {
        const updates = {};
        let remaining = excess;
        for (const line of giftLines) {
          if (remaining <= 0) break;
          const cut = Math.min(line.quantity, remaining);
          updates[line.key] = line.quantity - cut;
          remaining -= cut;
        }

        const cartData = await this.postCartUpdate(updates);
        if (cartData) return cartData;

        const removeAll = giftLines.reduce((acc, line) => ({ ...acc, [line.key]: 0 }), {});
        const clearedCart = await this.postCartUpdate(removeAll);
        if (clearedCart) this.rerunRequested = true;
        return clearedCart;
      }

      /**
       * Updates cart with new quantities
       * @param {string|null} id - Variant ID to update
       * @param {number} quantity - New quantity
       * @param {Array|null} ids - Array of variant IDs to update to quantity 0
       * @returns {Promise<Object>} - Cart data
       */
      async updateCart(id, quantity, ids = null) {
        let updates;
        if (ids) {
          updates = ids.reduce((a, v) => ({ ...a, [v]: 0 }), {});
        } else {
          updates = {
            [id]: quantity,
          };
        }

        return this.postCartUpdate(updates);
      }

      /**
       * POSTs cart/update.js. Keys may be variant ids or line item keys.
       * @param {Object} updates - { variantIdOrLineKey: quantity }
       * @returns {Promise<Object|null>} - cart data, or null on failure
       */
      async postCartUpdate(updates) {
        let formData = JSON.stringify({ updates });
        if (this.cart) {
          const sections = ['CartDrawer'];
          const sections_url = window.location.pathname;
          formData = JSON.stringify({ updates, sections, sections_url });
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
          }

          this.cartUpdateFailure = false;
          this.hideWarningMessage();
          return await response.json();
        } catch (error) {
          this.cartUpdateFailure = true;
          this.showWarningMessage('Failed to update your cart. Please try again.');
          console.error('Cart update error:', error);
          return null;
        }
      }

      /**
       * Reads the live cart.
       * @returns {Promise<Object|null>} - cart.js data, or null on failure
       */
      async fetchCart() {
        try {
          const response = await fetch(window.Shopify.routes.root + 'cart.js');
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          console.error('Failed to read cart:', error);
          return null;
        }
      }

      /**
       * Tells the drawer / other campaign instances the cart changed. The
       * `from` id lets this instance skip its own event.
       * @param {Object} cartData
       */
      dispatchCartUpdated(cartData) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { cart: cartData, source: 'campaign', from: this.instanceId }
        }));
      }

      /**
       * Add items to cart
       * @param {string} id - Variant ID to add
       * @param {number} quantity - Quantity to add
       * @param {string} campaign_name - Campaign name
       * @returns {Promise<Object>} - Cart data
       */
      async addToCart(id, quantity, campaign_name) {
        if (!id || quantity <= 0) {
          console.warn('Invalid addToCart parameters', { id, quantity });
          return null;
        }

        const properties = { "_campaign": campaign_name, "_automatic_gift": true };
        const formData = { items: [{ id: id, quantity: quantity, properties }] };

        if (this.cart) {
          formData.sections = ['CartDrawer'];
          formData.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          });

          if (!response.ok) {
            throw new Error(`Add to cart failed: ${response.status} ${response.statusText}`);
          }

          this.cartUpdateFailure = false;
          this.hideWarningMessage();
          return await response.json();
        } catch (error) {
          this.cartUpdateFailure = true;
          this.showWarningMessage('Failed to add items to your cart. Please try again.');
          console.error('Add to cart error:', error);
          return null;
        }
      }
    }
  );
}
